// AuthContext.js - FINAL FIXED VERSION
import { createContext, useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';

// ==================== COMPREHENSIVE CONSTANTS ====================
const DEFAULT_USER = {
  name: 'eEvolve-Guest',
  avatar: '',
  credits: 3,
  email: '',
  _id: null,
  isSubscribed: false,
  subscription: {
    status: 'inactive',
    planId: null,
    planName: null,
    activeFrom: null,
    nextBillingDate: null,
    lastCreditRefresh: null,
    autoRenew: true,
    razorpaySubscriptionId: null,
    razorpayCustomerId: null,
    razorpayPaymentId: null,
    razorpayOrderId: null,
    trialEndsAt: null
  },
  paymentHistory: [],
  transactions: [],
  usage: {
    scenariosCreated: 0,
    lastActive: null,
    totalCreditsUsed: 0,
    featuresUsed: {},
    lastLogin: null,
    sessionCount: 0
  },
  preferences: {
    notifications: true,
    autoRenew: true,
    theme: 'light',
    language: 'en',
    emailUpdates: true
  },
  statistics: {
    totalSpent: 0,
    totalCreditsPurchased: 0,
    favoriteFeatures: [],
    averageSessionTime: 0
  }
};

const AUTH_STATUS = {
  LOADING: 'loading',
  AUTHENTICATED: 'authenticated',
  UNAUTHENTICATED: 'unauthenticated',
  TRANSITIONING: 'transitioning',
  ERROR: 'error',
  EXPIRED: 'expired',
  SUSPENDED: 'suspended'
};

const SUBSCRIPTION_PLANS = {
  monthly: {
    id: 'monthly',
    name: 'Monthly Pro',
    price: 9999,
    credits: 3,
    interval: 'month',
    features: ['All Services', '3 Monthly Credits', 'Priority Support']
  },
  yearly: {
    id: 'yearly',
    name: 'Yearly Pro',
    price: 99999,
    credits: 36,
    interval: 'year',
    features: ['All Services', '36 Credits', 'Priority Support', 'Save 17%']
  }
};

const CREDIT_PACKAGES = {
  basic: { credits: 10, price: 100, bonus: 0 },
  pro: { credits: 25, price: 200, bonus: 5 },
  enterprise: { credits: 100, price: 700, bonus: 30 }
};

// ==================== CONTEXT CREATION ====================
export const AuthContext = createContext();

// ==================== ENHANCED PROVIDER COMPONENT ====================
export const AuthProvider = ({ children }) => {
  const [authState, setAuthState] = useState({
    user: null,
    token: null,
    status: AUTH_STATUS.LOADING,
    lastAction: null,
    error: null,
    lastUpdated: null,
    sessionStart: null
  });

  const [initializing, setInitializing] = useState(true);
  const navigate = useNavigate();
  const initializedRef = useRef(false);
  const refreshIntervalRef = useRef(null);
  const syncIntervalRef = useRef(null);
  const analyticsRef = useRef({ events: [], pageViews: [] });

  // ==================== ENHANCED STATE MANAGEMENT ====================

  const setUser = useCallback((userData) => {
    setAuthState(prev => {
      const newUserData = typeof userData === 'function' ? userData(prev.user) : userData;
      
      if (!newUserData) {
        console.warn('⚠️ setUser called with null/undefined data');
        return prev;
      }

      // ✅ CRITICAL: Use ONLY database values, no defaults
      const finalUserData = {
        // ✅ DIRECT DATABASE VALUES - NO DEFAULTS
        ...newUserData,
        
        // ✅ Only provide fallbacks for absolutely required fields
        credits: newUserData.credits !== undefined ? newUserData.credits : 0,
        isSubscribed: newUserData.isSubscribed !== undefined ? newUserData.isSubscribed : false,
        
        // ✅ Subscription: Use database values directly
        subscription: newUserData.subscription || {
          status: 'inactive',
          planId: null,
          planName: null,
          activeFrom: null,
          nextBillingDate: null,
          lastCreditRefresh: null,
          autoRenew: true,
          razorpaySubscriptionId: null,
          razorpayCustomerId: null,
          razorpayPaymentId: null,
          razorpayOrderId: null,
          trialEndsAt: null
        }
      };

      console.log('🔍 setUser - DIRECT DATABASE VALUES:', {
        'Database isSubscribed': newUserData.isSubscribed,
        'Final isSubscribed': finalUserData.isSubscribed,
        'Database Subscription Status': newUserData.subscription?.status,
        'Final Subscription Status': finalUserData.subscription?.status,
        'Payment ID': finalUserData.subscription?.razorpayPaymentId,
        'User ID': finalUserData._id
      });
      
      return {
        ...prev,
        user: finalUserData,
        lastUpdated: new Date().toISOString()
      };
    });
  }, []);

  const updateAuthState = useCallback((updates) => {
    setAuthState(prev => ({
      ...prev,
      ...updates,
      lastUpdated: new Date().toISOString()
    }));
  }, []);

  // ==================== PERSISTENCE RESTORATION ====================
  useEffect(() => {
    const loadPersistedData = () => {
      try {
        const token = localStorage.getItem('authToken') || localStorage.getItem('token');
        const userData = localStorage.getItem('userData');
        
        if (token && userData) {
          const parsedUser = JSON.parse(userData);
          console.log('🔄 AuthContext: Loading persisted user data:', {
            userId: parsedUser._id,
            isSubscribed: parsedUser.isSubscribed,
            hasToken: !!token
          });
          
          // Set token immediately
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          // Set user data immediately from localStorage
          setUser(parsedUser);
          updateAuthState({
            token,
            status: AUTH_STATUS.AUTHENTICATED,
            lastAction: 'persisted_restore'
          });
          
          console.log('✅ Immediate persistence restoration complete');
        } else {
          console.log('🔍 No persisted auth data found');
          updateAuthState({ status: AUTH_STATUS.UNAUTHENTICATED });
        }
      } catch (error) {
        console.error('❌ Failed to load persisted auth data:', error);
        updateAuthState({ status: AUTH_STATUS.UNAUTHENTICATED });
      }
    };

    loadPersistedData();
  }, []);

  // ✅ ADD THIS: Save user data to localStorage whenever it changes
  useEffect(() => {
    if (authState.user && authState.token) {
      try {
        localStorage.setItem('userData', JSON.stringify(authState.user));
        console.log('💾 AuthContext: User data persisted to localStorage');
      } catch (error) {
        console.error('❌ Failed to persist user data:', error);
      }
    }
  }, [authState.user, authState.token]);

  // ==================== COMPREHENSIVE AUTH CLEANUP ====================
  const clearAuth = useCallback(() => {
    console.log('🔄 Clearing authentication data...');
    
    // Clear all storage
    localStorage.removeItem('authToken');
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
    localStorage.removeItem('sessionData');
    sessionStorage.removeItem('token');
    
    // Clear intervals
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
    if (syncIntervalRef.current) {
      clearInterval(syncIntervalRef.current);
    }
    
    // Clear axios headers
    delete axios.defaults.headers.common['Authorization'];
    
    // Reset state
    updateAuthState({
      user: null,
      token: null,
      status: AUTH_STATUS.UNAUTHENTICATED,
      lastAction: 'logout',
      error: null
    });

    // Track logout event
    analyticsRef.current.events.push({
      type: 'user_logout',
      timestamp: new Date().toISOString()
    });
  }, [updateAuthState]);

  // ==================== ADVANCED ERROR HANDLING ====================
  const handleError = useCallback((error, context = {}, customMessage = null) => {
    console.error(`🔴 Auth Error [${context.operation || 'unknown'}]:`, {
      error,
      context,
      timestamp: new Date().toISOString()
    });
    
    const errorDetails = {
      message: error.response?.data?.message || 
               error.response?.data?.error || 
               customMessage || 
               'An unexpected error occurred',
      code: error.response?.data?.code || error.code || 'UNKNOWN_ERROR',
      status: error.response?.status,
      operation: context.operation,
      timestamp: new Date().toISOString(),
      userFriendly: true
    };

    // Enhanced error categorization
    if (error.response?.status === 401) {
      errorDetails.type = 'AUTH_EXPIRED';
      errorDetails.solution = 'Please login again';
    } else if (error.response?.status === 403) {
      errorDetails.type = 'ACCESS_DENIED';
      errorDetails.solution = 'Insufficient permissions';
    } else if (error.code === 'NETWORK_ERROR') {
      errorDetails.type = 'NETWORK_ISSUE';
      errorDetails.solution = 'Check your internet connection';
    } else {
      errorDetails.type = 'SERVER_ERROR';
      errorDetails.solution = 'Please try again later';
    }

    // Track error in analytics
    analyticsRef.current.events.push({
      type: 'error',
      ...errorDetails,
      severity: 'high'
    });

    updateAuthState({
      status: AUTH_STATUS.ERROR,
      error: errorDetails.message,
      lastAction: 'error_occurred'
    });

    return errorDetails;
  }, [updateAuthState]);

  // ==================== ERROR MANAGEMENT ====================
  const clearError = useCallback(() => {
    updateAuthState(prev => ({
      ...prev,
      error: null,
      status: prev.status === AUTH_STATUS.ERROR ? AUTH_STATUS.UNAUTHENTICATED : prev.status
    }));
  }, [updateAuthState]);

  // ==================== ENHANCED TOKEN MANAGEMENT ====================
  const getToken = useCallback(() => {
    const token = authState.token || 
                  localStorage.getItem('token') || 
                  sessionStorage.getItem('token') ||
                  localStorage.getItem('authToken');
    
    if (!token) return null;

    // Validate token format and expiration
    try {
      const decoded = jwtDecode(token);
      const isExpired = decoded.exp && decoded.exp * 1000 < Date.now();
      
      if (isExpired) {
        console.warn('🟡 Token expired, clearing...');
        clearAuth();
        return null;
      }
      
      return token;
    } catch (error) {
      console.error('🔴 Invalid token format:', error);
      clearAuth();
      return null;
    }
  }, [authState.token, clearAuth]);

  const setToken = useCallback((token, remember = true) => {
    const storage = remember ? localStorage : sessionStorage;
    
    storage.setItem('token', token);
    localStorage.setItem('authToken', token); // Always keep backup
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    updateAuthState({ 
      token,
      lastAction: 'token_updated'
    });
  }, [updateAuthState]);

  // ==================== ENHANCED SUBSCRIPTION MANAGEMENT ====================
  const isSubscribed = useMemo(() => {
    // ✅ CRITICAL: Directly use database isSubscribed value - NO calculations
    const dbIsSubscribed = authState.user?.isSubscribed;
    
    console.log('🔍 FINAL isSubscribed check - DIRECT DATABASE VALUE:', {
      'Database isSubscribed': dbIsSubscribed,
      'User ID': authState.user?._id,
      'Using Value Directly': dbIsSubscribed
    });
    
    // ✅ SIMPLE: Return exactly what the database says
    return dbIsSubscribed === true;
  }, [authState.user?.isSubscribed, authState.user?._id]);

  const getSubscriptionDetails = useCallback(() => {
    const subscription = authState.user?.subscription;
    if (!subscription) return null;
    
    const plan = SUBSCRIPTION_PLANS[subscription.planId];
    const isActive = isSubscribed;
    const daysRemaining = subscription.nextBillingDate 
      ? Math.ceil((new Date(subscription.nextBillingDate) - new Date()) / (1000 * 60 * 60 * 24))
      : 0;
    
    return {
      ...subscription,
      plan,
      isActive,
      daysRemaining,
      isTrial: subscription.status === 'trial',
      isCanceled: subscription.status === 'canceled',
      isExpired: subscription.status === 'expired',
      hasGracePeriod: daysRemaining < 0 && daysRemaining >= -7
    };
  }, [authState.user?.subscription, isSubscribed]);

  // ==================== CRITICAL: FORCE IMMEDIATE USER REFRESH ====================
  const forceRefreshUser = useCallback(async () => {
    try {
      const token = getToken();
      if (!token) {
        console.log('❌ No token for force refresh');
        return null;
      }

      console.log('🔄 Force refreshing user data...');

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/me`,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
            'X-Force-Refresh': 'true'
          },
          timeout: 10000,
          params: { _t: Date.now() }
        }
      );

      if (response.data.success && response.data.user) {
        const userData = response.data.user;
        
        console.log('🔍 Force Refresh - Database User Data:', {
          'Database isSubscribed': userData.isSubscribed,
          'Subscription Status': userData.subscription?.status,
          'User ID': userData._id
        });
        
        // Update AuthContext immediately
        setUser(userData);
        
        // Update localStorage
        localStorage.setItem('userData', JSON.stringify(userData));
        
        console.log('✅ User data force-refreshed:', {
          isSubscribed: userData.isSubscribed,
          subscription: userData.subscription?.status,
          credits: userData.credits
        });

        return userData;
      }
      
      throw new Error('Invalid response format');
    } catch (error) {
      console.error('❌ Force refresh failed:', error);
      
      // Fallback to localStorage
      const localUser = JSON.parse(localStorage.getItem('userData') || '{}');
      if (localUser._id) {
        setUser(localUser);
        return localUser;
      }
      
      return null;
    }
  }, [getToken, setUser]);

  // ==================== CRITICAL: IMMEDIATE SUBSCRIPTION STATUS UPDATE ====================
  const updateSubscriptionStatus = useCallback((subscriptionData) => {
    console.log('🔄 Updating subscription status immediately:', subscriptionData);
    
    setUser(prevUser => {
      if (!prevUser) return prevUser;
      
      const updatedUser = {
        ...prevUser,
        // ✅ CRITICAL: Set isSubscribed to true (database field)
        isSubscribed: true,
        subscription: {
          ...prevUser.subscription,
          status: 'active',
          planId: subscriptionData.planId,
          planName: subscriptionData.planName,
          activeFrom: subscriptionData.startDate || new Date().toISOString(),
          nextBillingDate: subscriptionData.endDate,
          lastCreditRefresh: new Date().toISOString(),
          autoRenew: true
        },
        credits: (prevUser.credits || 0) + (subscriptionData.creditsAdded || 0)
      };
      
      // Update localStorage immediately
      localStorage.setItem('userData', JSON.stringify(updatedUser));
      console.log('✅ Subscription status updated in AuthContext and localStorage');
      
      return updatedUser;
    });
  }, [setUser]);

  // ==================== ADVANCED USER PROFILE MANAGEMENT ====================
  const updateUser = useCallback(async (options = {}) => {
    const { 
      forceRefresh = false, 
      silent = false,
      retryCount = 0,
      background = false
    } = options;
    
    try {
      const token = getToken();
      if (!token) {
        if (!silent) {
          updateAuthState({ status: AUTH_STATUS.UNAUTHENTICATED });
        }
        console.log('❌ No token available for updateUser');
        return null;
      }

      if (!silent && !background) {
        updateAuthState({ status: AUTH_STATUS.TRANSITIONING });
      }

      console.log('🔄 Fetching user profile from server...', { 
        forceRefresh, 
        silent, 
        background,
        retryCount 
      });

      // ✅ Try multiple endpoints with proper error handling
      const endpoints = [
        `${process.env.REACT_APP_API_BASE_URL}/api/users/me`,
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/me`,
        `${process.env.REACT_APP_API_BASE_URL}/api/users/profile`
      ];

      let response = null;
      let lastError = null;

      for (const endpoint of endpoints) {
        try {
          console.log(`🔗 Trying endpoint: ${endpoint}`);
          response = await axios.get(endpoint, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Cache-Control': 'no-cache',
              'Pragma': 'no-cache'
            },
            timeout: 15000,
            params: forceRefresh ? { _t: Date.now(), source: 'force_refresh' } : {}
          });

          // Check if response has valid data
          if (response.data && (response.data.user || response.data.data || response.data._id)) {
            console.log(`✅ Success with endpoint: ${endpoint}`);
            break;
          } else {
            throw new Error('Invalid response format');
          }
        } catch (err) {
          lastError = err;
          console.warn(`❌ Endpoint ${endpoint} failed:`, err.response?.data || err.message);
          // Continue to next endpoint
        }
      }

      if (!response || !response.data) {
        throw new Error(lastError?.message || 'All endpoints failed to return valid data');
      }

      // ✅ CRITICAL: Extract user data from ALL possible response formats
      const rawUserData = response.data.user || response.data.data || response.data;
      
      // ✅ VALIDATE: Ensure we have at least user ID
      if (!rawUserData._id && !rawUserData.id && !rawUserData.userId) {
        throw new Error('Invalid user data: No user ID found');
      }

      console.log('🔍 updateUser - RAW Database Response Analysis:', {
        'Has isSubscribed Field': 'isSubscribed' in rawUserData,
        'Raw isSubscribed Value': rawUserData.isSubscribed,
        'Raw Subscription Status': rawUserData.subscription?.status,
        'User ID': rawUserData._id || rawUserData.id || rawUserData.userId,
        'Response Structure': Object.keys(rawUserData)
      });

      // ✅ ENHANCED: Create validated user data with ALL database fields
      const validatedUserData = {
        // ✅ CRITICAL: Preserve ALL database fields as-is
        ...rawUserData,
        
        // ✅ Ensure required fields with proper fallbacks
        _id: rawUserData._id || rawUserData.id || rawUserData.userId,
        email: rawUserData.email || authState.user?.email || '',
        name: rawUserData.name || authState.user?.name || 'User',
        credits: rawUserData.credits ?? (authState.user?.credits || 0),
        
        // ✅ CRITICAL: Use database isSubscribed EXACTLY as received
        isSubscribed: rawUserData.isSubscribed !== undefined 
          ? rawUserData.isSubscribed 
          : (authState.user?.isSubscribed !== undefined ? authState.user.isSubscribed : false),
        
        // ✅ CRITICAL: Preserve subscription object exactly from database
        subscription: {
          ...DEFAULT_USER.subscription,
          ...rawUserData.subscription // This OVERRIDES defaults with database values
        },
        
        // Preserve other objects with proper merging
        paymentHistory: rawUserData.paymentHistory || authState.user?.paymentHistory || [],
        transactions: rawUserData.transactions || authState.user?.transactions || [],
        usage: {
          ...DEFAULT_USER.usage,
          ...(rawUserData.usage || {}),
          ...(authState.user?.usage || {})
        },
        preferences: {
          ...DEFAULT_USER.preferences,
          ...(rawUserData.preferences || {}),
          ...(authState.user?.preferences || {})
        },
        statistics: {
          ...DEFAULT_USER.statistics,
          ...(rawUserData.statistics || {}),
          ...(authState.user?.statistics || {})
        }
      };

      // ✅ Set user data with validated structure
      setUser(validatedUserData);
      
      if (!silent && !background) {
        updateAuthState({
          status: AUTH_STATUS.AUTHENTICATED,
          lastAction: 'user_updated',
          error: null
        });
      }

      console.log('✅ User profile updated successfully:', { 
        'Database isSubscribed': rawUserData.isSubscribed,
        'Final isSubscribed': validatedUserData.isSubscribed,
        'Subscription Status': validatedUserData.subscription?.status,
        'Credits': validatedUserData.credits,
        'Source': background ? 'background_sync' : 'user_action'
      });

      // Track successful update
      analyticsRef.current.events.push({
        type: 'user_data_updated',
        success: true,
        source: background ? 'background' : 'manual',
        timestamp: new Date().toISOString()
      });

      return validatedUserData;

    } catch (error) {
      console.error('❌ updateUser failed:', error);
      
      // Enhanced retry logic with exponential backoff
      if (retryCount < 2) {
        const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s, 4s
        console.log(`🔄 Retrying updateUser in ${delay}ms (${retryCount + 1}/2)...`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return updateUser({ ...options, retryCount: retryCount + 1 });
      }
      
      // Handle specific error types
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.warn('🔐 Authentication expired during updateUser');
        if (!options.silent) {
          clearAuth();
          navigate('/login');
        }
      } else if (!options.silent) {
        handleError(error, { operation: 'update_user' }, 'Failed to update user data');
      }
      
      // Track failed update
      analyticsRef.current.events.push({
        type: 'user_data_update_failed',
        error: error.message,
        retryCount: options.retryCount,
        timestamp: new Date().toISOString()
      });
      
      return null;
    }
  }, [getToken, setUser, updateAuthState, authState.user, clearAuth, navigate, handleError]);

  // ==================== COMPREHENSIVE SUBSCRIPTION METHODS ====================
  const forceSubscriptionUpdate = useCallback(async () => {
    try {
      console.log('🔄 Force updating subscription status...', {
        currentStatus: authState.user?.subscription?.status,
        userId: authState.user?._id
      });
      
      const updatedUser = await updateUser({ 
        forceRefresh: true, 
        silent: true, 
        background: true 
      });
      
      console.log('🔍 Updated user data:', {
        isSubscribed: updatedUser?.isSubscribed,
        subscriptionStatus: updatedUser?.subscription?.status,
        nextBillingDate: updatedUser?.subscription?.nextBillingDate,
        planId: updatedUser?.subscription?.planId
      });
      
      if (updatedUser?.isSubscribed === true) {
        console.log('✅ Subscription status confirmed as ACTIVE');
        
        // Track successful subscription sync
        analyticsRef.current.events.push({
          type: 'subscription_sync_success',
          status: 'active',
          planId: updatedUser.subscription.planId,
          timestamp: new Date().toISOString()
        });
        
        return true;
      }
      
      console.log('ℹ️ Subscription status:', updatedUser?.isSubscribed);
      return false;
    } catch (error) {
      console.error('❌ Force subscription update failed:', error);
      return false;
    }
  }, [updateUser, authState.user]);

  const checkAndSyncSubscription = useCallback(async () => {
    try {
      const token = getToken();
      if (!token) {
        console.log('❌ No token for subscription sync');
        return false;
      }

      console.log('🔄 Checking and syncing subscription status...', {
        userId: authState.user?._id,
        currentIsSubscribed: authState.user?.isSubscribed
      });

      // First, check for emergency payments in localStorage
      const emergencyPayment = localStorage.getItem('emergencyPayment');
      if (emergencyPayment) {
        try {
          const paymentData = JSON.parse(emergencyPayment);
          console.log('🔄 Found emergency payment, processing...', paymentData);
          
          // Apply emergency payment locally
          setUser(prev => ({
            ...prev,
            credits: (prev.credits || 0) + paymentData.plan.totalCredits,
            isSubscribed: true, // ✅ Set isSubscribed directly
            subscription: {
              status: 'active',
              planId: paymentData.plan.id,
              planName: paymentData.plan.name,
              activeFrom: new Date().toISOString(),
              nextBillingDate: paymentData.plan.interval === 'monthly' 
                ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
                : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
              lastCreditRefresh: new Date().toISOString(),
              autoRenew: true
            }
          }));

          // Clear emergency payment
          localStorage.removeItem('emergencyPayment');
          
          console.log('✅ Emergency payment applied locally');
          
          // Track emergency payment application
          analyticsRef.current.events.push({
            type: 'emergency_payment_applied',
            planId: paymentData.plan.id,
            creditsAdded: paymentData.plan.totalCredits,
            timestamp: new Date().toISOString()
          });
          
          return true;
        } catch (parseError) {
          console.error('❌ Failed to parse emergency payment:', parseError);
          localStorage.removeItem('emergencyPayment');
        }
      }

      // Force server sync for accurate status
      const result = await forceSubscriptionUpdate();
      
      console.log('🔍 Subscription sync result:', {
        success: result,
        currentIsSubscribed: authState.user?.isSubscribed,
        isSubscribed
      });
      
      return result;
    } catch (error) {
      console.error('❌ Subscription sync failed:', error);
      return false;
    }
  }, [getToken, authState.user, setUser, forceSubscriptionUpdate, isSubscribed]);

  const purchaseSubscription = useCallback(async (planId) => {
    try {
      const token = getToken();
      if (!token) throw new Error('No authentication token available');

      console.log(`🔄 Creating subscription order for plan: ${planId}`, {
        userId: authState.user?._id,
        userEmail: authState.user?.email
      });

      const plan = SUBSCRIPTION_PLANS[planId];
      if (!plan) {
        throw new Error(`Invalid plan ID: ${planId}`);
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/subscriptions/create-order`,
        { 
          planId,
          metadata: {
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            source: 'web_app'
          }
        },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'X-Request-Type': 'subscription_creation'
          },
          timeout: 20000
        }
      );

      if (response.data.success) {
        console.log('✅ Subscription order created successfully:', {
          orderId: response.data.order.id,
          planId: planId,
          amount: response.data.order.amount
        });

        // Track subscription creation
        analyticsRef.current.events.push({
          type: 'subscription_order_created',
          planId,
          orderId: response.data.order.id,
          amount: response.data.order.amount,
          timestamp: new Date().toISOString()
        });

        return {
          ...response.data,
          planDetails: plan
        };
      }
      
      throw new Error(response.data.error || 'Failed to create subscription order');
    } catch (error) {
      handleError(
        error, 
        { operation: 'purchase_subscription' }, 
        'Failed to create subscription. Please try again.'
      );

      // Track subscription failure
      analyticsRef.current.events.push({
        type: 'subscription_creation_failed',
        planId,
        error: error.message,
        timestamp: new Date().toISOString()
      });

      throw error;
    }
  }, [getToken, authState.user, handleError]);

  const verifySubscriptionPayment = useCallback(async (paymentData) => {
    try {
      const token = getToken();
      if (!token) throw new Error('No authentication token available');

      console.log('🔄 Verifying subscription payment...', {
        orderId: paymentData.razorpay_order_id,
        paymentId: paymentData.razorpay_payment_id
      });

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/subscriptions/verify-payment`,
        {
          ...paymentData,
          verificationContext: {
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            source: 'web_app'
          }
        },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'X-Request-Type': 'subscription_verification'
          },
          timeout: 25000
        }
      );

      if (response.data.success) {
        console.log('✅ Subscription payment verified successfully:', {
          paymentId: paymentData.razorpay_payment_id,
          planId: paymentData.planId
        });

        // ✅ CRITICAL: Force immediate user data refresh after payment
        await forceRefreshUser();

        // Track successful subscription
        analyticsRef.current.events.push({
          type: 'subscription_activated',
          planId: paymentData.planId,
          paymentId: paymentData.razorpay_payment_id,
          amount: response.data.data?.amount,
          timestamp: new Date().toISOString()
        });

        return response.data;
      }
      
      throw new Error(response.data.error || 'Payment verification failed');
    } catch (error) {
      handleError(
        error, 
        { operation: 'verify_subscription_payment' }, 
        'Payment verification failed. Please contact support.'
      );

       // Track failed registration
      analyticsRef.current.events.push({
        type: 'user_registration',
        success: false,
        error: error.message,
        timestamp: new Date().toISOString()
      });  // ← Fixed: Added closing brace and removed extra parenthesis

      throw error;
    }
  }, [updateAuthState, setToken, setUser, navigate, handleError]);


  // ==================== ENHANCED CREDIT MANAGEMENT ====================
  const deductCredits = useCallback(async (creditsToDeduct = 1, action = 'feature_usage', metadata = {}) => {
    try {
      const token = getToken();
      if (!token) throw new Error('No authentication token available');

      if (authState.user?.credits < creditsToDeduct) {
        throw new Error(`Insufficient credits. Available: ${authState.user.credits}, Required: ${creditsToDeduct}`);
      }

      console.log(`🔄 Deducting ${creditsToDeduct} credits for: ${action}`, metadata);

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/deduct-credits`,
        { 
          credits: creditsToDeduct, 
          action,
          metadata: {
            ...metadata,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent
          }
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000
        }
      );

      if (response.data.success && response.data.user) {
        setUser(response.data.user);

        // Track credit usage
        analyticsRef.current.events.push({
          type: 'credits_deducted',
          amount: creditsToDeduct,
          action,
          remaining: response.data.user.credits,
          timestamp: new Date().toISOString()
        });

        return {
          success: true,
          newBalance: response.data.user.credits,
          transactionId: response.data.transactionId
        };
      }
      
      throw new Error(response.data.error || 'Failed to deduct credits');
    } catch (error) {
      handleError(
        error, 
        { operation: 'deduct_credits' }, 
        'Failed to process credit deduction'
      );

      throw error;
    }
  }, [getToken, authState.user, setUser, handleError]);

  const hasEnoughCredits = useCallback((creditsRequired = 1, feature = 'unknown') => {
    const available = authState.user?.credits || 0;
    const hasEnough = available >= creditsRequired;
    
    // Track credit check for analytics
    if (feature !== 'unknown') {
      analyticsRef.current.events.push({
        type: 'credit_check',
        feature,
        required: creditsRequired,
        available,
        hasEnough,
        timestamp: new Date().toISOString()
      });
    }
    
    return hasEnough;
  }, [authState.user?.credits]);

  // ==================== ENHANCED AUTHENTICATION METHODS ====================
  const login = useCallback(async (credentials, options = {}) => {
    const { remember = true, redirect = true } = options;
    
    try {
      updateAuthState({ status: AUTH_STATUS.TRANSITIONING, error: null });
      
      console.log('🔄 Attempting user login...', {
        email: credentials.email,
        remember,
        timestamp: new Date().toISOString()
      });

      const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/login`,
        {
          ...credentials,
          loginContext: {
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            source: 'web_app'
          }
        },
        { timeout: 15000 }
      );

      if (!data.token) {
        throw new Error('No authentication token received from server');
      }

      const token = data.token;
      setToken(token, remember);

      // ✅ CRITICAL: Extract RAW user data from login response
      const rawUserData = data.user || data.data || data;
      
      console.log('🔍 Login - RAW Database Response:', {
        'Raw isSubscribed': rawUserData.isSubscribed,
        'Raw Subscription': rawUserData.subscription,
        'Raw User ID': rawUserData._id
      });

      // ✅ Set user data IMMEDIATELY from raw database response
      if (rawUserData._id) {
        setUser(rawUserData); // ✅ This will use ONLY database values
        console.log('✅ User data set directly from database response');
      }

      // ✅ Update auth state to authenticated
      updateAuthState({ 
        status: AUTH_STATUS.AUTHENTICATED,
        error: null,
        lastAction: 'login',
        sessionStart: new Date().toISOString()
      });

      // ✅ CRITICAL FIX: Skip the updateUser call that causes 404
      // Only try to fetch complete profile if we don't have subscription data
      if (!rawUserData.subscription || !rawUserData.subscription.status) {
        try {
          console.log('🔄 Attempting to fetch complete profile (missing subscription data)...');
          await updateUser({ forceRefresh: true, silent: true });
        } catch (profileError) {
          console.warn('⚠️ Profile fetch failed, but login succeeded with subscription data');
          // Non-critical - we already have user data with subscription
        }
      } else {
        console.log('✅ Skipping profile fetch - already have complete subscription data');
      }

      console.log('✅ Login completed successfully:', {
        userId: rawUserData._id,
        isSubscribed: rawUserData.isSubscribed,
        subscriptionStatus: rawUserData.subscription?.status
      });

      // ✅ CRITICAL: Add immediate debug to check current state
      console.log('🔍 IMMEDIATE POST-LOGIN AUTH STATE:', {
        userIsSubscribed: authState.user?.isSubscribed,
        calculatedIsSubscribed: isSubscribed,
        userData: authState.user
      });

      if (redirect) {
        navigate('/ROIAnalyzer');
      }

      return { success: true, user: rawUserData };
    } catch (error) {
      console.error('❌ Login error:', error);
      handleError(error, { operation: 'user_login' }, 'Login failed.');
      throw error;
    }
  }, [updateAuthState, setToken, setUser, updateUser, navigate, handleError, authState.user, isSubscribed]);

  const register = useCallback(async (userData, options = {}) => {
    const { redirect = true } = options;
    
    try {
      updateAuthState({ status: AUTH_STATUS.TRANSITIONING, error: null });
      
      console.log('🔄 Attempting user registration...', {
        email: userData.email,
        name: userData.name,
        timestamp: new Date().toISOString()
      });

      const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/register`,
        {
          ...userData,
          registrationContext: {
            userAgent: navigator.userAgent,
            timestamp: new Date().toISOString(),
            source: 'web_app',
            referrer: document.referrer
          }
        },
        { timeout: 15000 }
      );

      if (!data.token) {
        throw new Error('No authentication token received after registration');
      }

      const token = data.token;
      setToken(token, true);

      // Extract and set user data
      const registeredUser = data.user || data.data || data;
      const finalUserData = registeredUser.user || registeredUser;
      const userId = finalUserData._id || finalUserData.id || finalUserData.userId;
      
      if (userId) {
        setUser({
          _id: userId,
          email: finalUserData.email || userData.email,
          name: finalUserData.name || userData.name,
          credits: finalUserData.credits || 3,
          isSubscribed: finalUserData.isSubscribed || false, // ✅ Ensure false for new users
          avatar: finalUserData.avatar || '',
          subscription: {
            ...DEFAULT_USER.subscription,
            ...(finalUserData.subscription || {})
          },
          paymentHistory: finalUserData.paymentHistory || [],
          transactions: finalUserData.transactions || [],
          usage: {
            ...DEFAULT_USER.usage,
            ...(finalUserData.usage || {}),
            lastLogin: new Date().toISOString(),
            sessionCount: 1
          },
          preferences: finalUserData.preferences || DEFAULT_USER.preferences,
          statistics: finalUserData.statistics || DEFAULT_USER.statistics
        });
      }

      updateAuthState({ 
        token,
        status: AUTH_STATUS.AUTHENTICATED,
        error: null,
        lastAction: 'register',
        sessionStart: new Date().toISOString()
      });

      console.log('✅ User registered successfully:', {
        userId,
        email: userData.email,
        credits: finalUserData.credits,
        isSubscribed: finalUserData.isSubscribed
      });

      // Track successful registration
      analyticsRef.current.events.push({
        type: 'user_registration',
        success: true,
        subscriptionStatus: 'inactive',
        timestamp: new Date().toISOString()
      });

      if (redirect) {
        navigate('/');
      }

      return { success: true, user: finalUserData };
    } catch (error) {
      console.error('❌ Registration error:', error.response?.data || error.message);
      
      handleError(
        error, 
        { operation: 'user_registration' }, 
        'Registration failed. Please try again with different credentials.'
      );

      // Track failed registration
      analyticsRef.current.events.push({
        type: 'user_registration',
        success: false,
        error: error.message,
        timestamp: new Date().toISOString()
      });

      throw error;
    }
  }, [updateAuthState, setToken, setUser, navigate, handleError]);

  const logout = useCallback(async (options = {}) => {
    const { redirect = true, reason = 'user_initiated' } = options;
    
    try {
      updateAuthState({ status: AUTH_STATUS.TRANSITIONING });
      
      console.log('🔄 Logging out user...', { reason, timestamp: new Date().toISOString() });

      // Attempt server logout
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/logout`,
        {
          logoutContext: {
            reason,
            timestamp: new Date().toISOString(),
            sessionDuration: authState.sessionStart 
              ? Date.now() - new Date(authState.sessionStart).getTime()
              : null
          }
        },
        {
          headers: { Authorization: `Bearer ${getToken()}` },
          validateStatus: (status) => status === 200 || status === 401 || status === 404,
          timeout: 5000
        }
      );
    } catch (error) {
      // Log but don't throw error for logout failures
      console.warn('⚠️ Server logout may have failed:', error.message);
    } finally {
      // Always clear local auth state
      clearAuth();
      
      // Track logout
      analyticsRef.current.events.push({
        type: 'user_logout',
        reason,
        timestamp: new Date().toISOString()
      });

      if (redirect) {
        navigate('/login');
      }
    }
  }, [updateAuthState, getToken, clearAuth, navigate, authState.sessionStart]);

  // ==================== ENHANCED PROFILE MANAGEMENT ====================
  const updateUserProfile = useCallback(async (profileData) => {
    try {
      const token = getToken();
      if (!token) throw new Error('No authentication token available');

      console.log('🔄 Updating user profile...', {
        fields: Object.keys(profileData),
        timestamp: new Date().toISOString()
      });

      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/profile`,
        {
          ...profileData,
          updateContext: {
            timestamp: new Date().toISOString(),
            source: 'web_app'
          }
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000
        }
      );

      if (response.data.success && response.data.user) {
        setUser(response.data.user);
        
        // Track profile update
        analyticsRef.current.events.push({
          type: 'profile_updated',
          fields: Object.keys(profileData),
          timestamp: new Date().toISOString()
        });

        return response.data.user;
      }
      
      throw new Error(response.data.error || 'Failed to update profile');
    } catch (error) {
      handleError(
        error, 
        { operation: 'update_user_profile' }, 
        'Failed to update profile. Please try again.'
      );
      throw error;
    }
  }, [getToken, setUser, handleError]);

  const updateUserPreferences = useCallback(async (preferences) => {
    try {
      const token = getToken();
      if (!token) throw new Error('No authentication token available');

      console.log('🔄 Updating user preferences...', {
        preferences,
        timestamp: new Date().toISOString()
      });

      const response = await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/preferences`,
        {
          preferences,
          updateContext: {
            timestamp: new Date().toISOString()
          }
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000
        }
      );

      if (response.data.success && response.data.user) {
        setUser(response.data.user);
        return response.data.user;
      }
      
      throw new Error(response.data.error || 'Failed to update preferences');
    } catch (error) {
      handleError(
        error, 
        { operation: 'update_user_preferences' }, 
        'Failed to update preferences.'
      );
      throw error;
    }
  }, [getToken, setUser, handleError]);

  // ==================== ADVANCED FEATURE ACCESS CONTROL ====================
  const checkFeatureAccess = useCallback((featureId, options = {}) => {
    const { creditsRequired = 0, subscriptionRequired = false } = options;
    
    if (!authState.user) {
      return {
        accessible: false,
        reason: 'not_authenticated',
        message: 'Please log in to access this feature'
      };
    }

    if (subscriptionRequired && !isSubscribed) {
      return {
        accessible: false,
        reason: 'subscription_required',
        message: 'This feature requires an active subscription'
      };
    }

    if (creditsRequired > 0 && !hasEnoughCredits(creditsRequired, featureId)) {
      return {
        accessible: false,
        reason: 'insufficient_credits',
        message: `This feature requires ${creditsRequired} credit(s)`,
        available: authState.user.credits,
        required: creditsRequired
      };
    }

    // Check specific feature permissions
    const featurePermissions = {
      'advanced-analytics': isSubscribed,
      'pdf-export': isSubscribed,
      'multi-scenario': isSubscribed || hasEnoughCredits(1, featureId),
      '3d-visualization': isSubscribed,
      'ai-insights': isSubscribed
    };

    if (featurePermissions[featureId] === false) {
      return {
        accessible: false,
        reason: 'feature_restricted',
        message: 'This feature is not available with your current plan'
      };
    }

    return {
      accessible: true,
      reason: 'access_granted',
      message: 'Feature accessible'
    };
  }, [authState.user, isSubscribed, hasEnoughCredits]);

  // ==================== COMPREHENSIVE ANALYTICS & MONITORING ====================
  const trackUserAction = useCallback((action, metadata = {}) => {
    const event = {
      type: 'user_action',
      action,
      userId: authState.user?._id,
      timestamp: new Date().toISOString(),
      ...metadata
    };

    analyticsRef.current.events.push(event);

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.log('📊 User Action:', event);
    }

    // In production, you would send to analytics service
    if (process.env.NODE_ENV === 'production') {
      // Example: send to analytics API
      // axios.post('/api/analytics/track', event).catch(console.error);
    }
  }, [authState.user?._id]);

  const getAnalytics = useCallback(() => {
    return {
      events: [...analyticsRef.current.events],
      pageViews: [...analyticsRef.current.pageViews],
      sessionStart: authState.sessionStart,
      userStatus: authState.status,
      subscriptionStatus: authState.user?.subscription?.status
    };
  }, [authState.sessionStart, authState.status, authState.user?.subscription?.status]);

  // ==================== ENHANCED INITIALIZATION & BACKGROUND SYNC ====================
  useEffect(() => {
    console.log('🔍 FINAL isSubscribed Value Debug:', {
      'Database isSubscribed': authState.user?.isSubscribed,
      'Calculated isSubscribed': isSubscribed,
      'Subscription Status': authState.user?.subscription?.status,
      'User ID': authState.user?._id,
      'Auth Status': authState.status
    });
  }, [authState.user?.isSubscribed, isSubscribed, authState.user?._id, authState.status]);

  // ✅ FIXED: Enhanced initialization with proper authentication state
  useEffect(() => {
    if (initializedRef.current) return;
    
    const initializeAuth = async () => {
      try {
        console.log('🚀 Enhanced authentication initialization starting...');
        setInitializing(true);
        
        // Check if we have persisted data
        const token = getToken();
        const persistedUser = localStorage.getItem('userData');
        
        if (token && persistedUser) {
          console.log('✅ User already authenticated from persistence');
          // Auth state is already set from persistence useEffect
          setInitializing(false);
          initializedRef.current = true;
          return;
        }

        // No persisted data, set unauthenticated
        console.log('🔍 No persisted auth data found');
        updateAuthState({ status: AUTH_STATUS.UNAUTHENTICATED });
        
      } catch (error) {
        console.error('❌ Auth initialization failed:', error);
        updateAuthState({ status: AUTH_STATUS.UNAUTHENTICATED });
      } finally {
        setInitializing(false);
        initializedRef.current = true;
      }
    };

    initializeAuth();
  }, [getToken, updateAuthState]);

  // ==================== COMPREHENSIVE CONTEXT VALUE ====================
  const contextValue = useMemo(() => ({
    // User data
    user: authState.user,
    token: authState.token,
    _id: authState.user?._id,
    userId: authState.user?._id,
    email: authState.user?.email,
    name: authState.user?.name,
    credits: authState.user?.credits || 0,
    avatar: authState.user?.avatar,
    
    // Authentication status
    isAuthenticated: authState.status === AUTH_STATUS.AUTHENTICATED,
    authStatus: authState.status,
    lastAction: authState.lastAction,
    error: authState.error,
    initializing,
    
    // Subscription management
    isSubscribed,
    subscription: authState.user?.subscription,
    getSubscriptionDetails,
    subscriptionPlans: SUBSCRIPTION_PLANS,
    
    // Payment & credit management
    paymentHistory: authState.user?.paymentHistory || [],
    transactions: authState.user?.transactions || [],
    hasEnoughCredits,
    creditPackages: CREDIT_PACKAGES,
    
    // User preferences & usage
    preferences: authState.user?.preferences || DEFAULT_USER.preferences,
    usage: authState.user?.usage || DEFAULT_USER.usage,
    statistics: authState.user?.statistics || DEFAULT_USER.statistics,
    
    // Core authentication methods
    login,
    logout,
    register,
    refreshUser: () => updateUser({ silent: true }),
    fetchUserProfile: () => updateUser({ silent: true }),
    updateUser,
    updateUserProfile,
    updateUserPreferences,
    updateCredits: (newCredits) => setUser(prev => ({ ...prev, credits: newCredits })),
    deductCredits,
    
    // ✅ CRITICAL NEW METHODS FOR SUBSCRIPTION MANAGEMENT
    forceRefreshUser,
    updateSubscriptionStatus,
    
    // Payment methods
    purchaseSubscription,
    handlePaymentSuccess: (paymentData) => updateUser({ silent: false }),
    verifySubscriptionPayment,
    
    // Subscription methods
    forceSubscriptionUpdate,
    checkAndSyncSubscription,
    
    // Feature access & permissions
    checkFeatureAccess,
    getFeatureAccess: (featureId) => checkFeatureAccess(featureId),
    
    // Analytics & monitoring
    trackUserAction,
    getAnalytics,
    
    // Utility methods
    clearError,
    getToken,
    getAuthState: () => ({ ...authState }),
    
    // Constants
    AUTH_STATUS,
    SUBSCRIPTION_PLANS,
    CREDIT_PACKAGES
    
  }), [
    authState,
    initializing,
    isSubscribed,
    getSubscriptionDetails,
    hasEnoughCredits,
    login,
    logout,
    register,
    updateUser,
    updateUserProfile,
    updateUserPreferences,
    setUser,
    deductCredits,
    purchaseSubscription,
    verifySubscriptionPayment,
    forceSubscriptionUpdate,
    checkAndSyncSubscription,
    checkFeatureAccess,
    trackUserAction,
    getAnalytics,
    clearError,
    getToken,
    forceRefreshUser,
    updateSubscriptionStatus
  ]);

  // ==================== LOADING STATE ====================
  if (initializing) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '1rem',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
      }}>
        <div style={{ 
          width: '60px', 
          height: '60px', 
          border: '4px solid rgba(255,255,255,0.3)',
          borderTop: '4px solid white',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
          Initializing Quantum ROI Analyzer...
        </div>
        <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>
          Loading subscription services and user profile
        </div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// ==================== HOOK EXPORT ====================
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;